import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * HomeDepartmentDuo — a two-vertical "explore the boutique" band.
 *
 * Shown on the home page ONLY once a second department is genuinely live
 * (see ShopHome's `showDepartmentDuo` gate), so the store reads as a
 * two-world boutique — Clothing AND Jewellery — instead of a clothing shop
 * with jewellery bolted on. Deliberately editorial (image + copy + arrow),
 * distinct from the utilitarian nav/menu, so it earns its place rather than
 * duplicating navigation.
 */
const COPY = {
    clothing: { eyebrow: "The wardrobe", line: "Sarees, lehengas, anarkalis & everyday ethnic wear." },
    jewellery: { eyebrow: "The adornment", line: "Kundan, pearls & temple silhouettes to finish the look." },
};

function DeptCard({ dept, i }) {
    const meta = COPY[dept.slug] || { eyebrow: "Explore", line: `Discover ${(dept.name || "").toLowerCase()}.` };
    return (
        <Link
            to={`/d/${dept.slug}`}
            data-testid={`home-dept-duo-${dept.slug}`}
            className="group relative overflow-hidden rounded-2xl border border-rosegold/25 bg-ivory-soft
                       shadow-[0_20px_50px_-40px_rgba(80,30,30,0.4)] hover:border-rosegold/50 transition-colors"
        >
            <div className="aspect-[4/5] sm:aspect-[3/4] overflow-hidden">
                {dept.cover_image ? (
                    <img
                        src={dept.cover_image}
                        alt={dept.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-rosegold/20 via-ivory-soft to-wine/15" />
                )}
            </div>
            {/* Ink scrim so the copy is legible over any image */}
            <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-ink/85 via-ink/40 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
                <p className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.28em] text-rosegold font-bold mb-1">
                    {meta.eyebrow}
                </p>
                <h3 className="font-display text-ivory text-xl sm:text-2xl leading-tight">{dept.name}</h3>
                <p className="mt-1 text-[11.5px] sm:text-[12.5px] text-ivory/80 leading-snug line-clamp-2 max-w-[26ch]">
                    {meta.line}
                </p>
                <span className="mt-2.5 inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.16em] text-ivory">
                    Explore
                    <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </span>
            </div>
        </Link>
    );
}

export default function HomeDepartmentDuo({ departments = [] }) {
    // Feature the two live storefront verticals in a deliberate order
    // (wardrobe first, adornment second) — ignore any others.
    const order = ["clothing", "jewellery"];
    const cards = order
        .map((slug) => departments.find((d) => d.slug === slug && d.status === "live"))
        .filter(Boolean);
    if (cards.length < 2) return null;

    return (
        <section className="mb-12 lg:mb-20" data-testid="home-department-duo">
            <header className="mb-6 lg:mb-8">
                <p className="text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold mb-3 flex items-center gap-3">
                    <span className="inline-block w-8 h-px bg-rosegold flex-shrink-0" />
                    Explore the boutique
                </p>
                <h2 className="font-display text-2xl sm:text-3xl text-ink leading-tight">
                    Two worlds, <span className="italic text-wine">one boutique</span>.
                </h2>
            </header>
            <div className="grid grid-cols-2 gap-3.5 sm:gap-5 lg:gap-6">
                {cards.map((d, i) => (
                    <DeptCard key={d.slug} dept={d} i={i} />
                ))}
            </div>
        </section>
    );
}
